/**
 * MC Collection Nav — Modern Class-Based AJAX Navigation
 *
 * Classes:
 *   MCCollectionNav   — Desktop sidebar toggle + AJAX navigation
 *   MCCollectionSort  — Desktop sorting via radio inputs
 *   MCMobileDrawer    — Mobile drawer, accordion, sorting & AJAX nav
 *
 */

/* ========================================================================
   Shared Helpers
   ======================================================================== */

/**
 * Update common page elements after an AJAX fetch.
 * @param {Document} doc – The parsed response document.
 */
const updatePageElements = (doc) => {
  // Browser tab title
  const newTitle = doc.querySelector("title");
  if (newTitle) {
    document.title = newTitle.textContent;
  }

  // Inline collection title
  const newCollectionTitle = doc.querySelector(
    ".collection__title, .title--primary",
  );
  const currentCollectionTitle = document.querySelector(
    ".collection__title, .title--primary",
  );
  if (newCollectionTitle && currentCollectionTitle) {
    currentCollectionTitle.textContent = newCollectionTitle.textContent;
  }

  // Hero / banner title (innerHTML preserves visually-hidden span)
  const newHeroTitle = doc.querySelector(".collection-hero__title");
  const currentHeroTitle = document.querySelector(".collection-hero__title");
  if (newHeroTitle && currentHeroTitle) {
    currentHeroTitle.innerHTML = newHeroTitle.innerHTML;
  }

  // MC Collection Hero — swap via the always-present [data-mc-hero] wrapper
  const newHeroWrap = doc.querySelector("[data-mc-hero]");
  const currentHeroWrap = document.querySelector("[data-mc-hero]");

  if (newHeroWrap && currentHeroWrap) {
    currentHeroWrap.innerHTML = newHeroWrap.innerHTML;

    // Scripts inserted via innerHTML don't execute — load the hero JS dynamically
    const needsSlider = currentHeroWrap.querySelector(
      "[data-mc-col-hero-slider]",
    );
    if (needsSlider) {
      if (window._mcHeroJSLoaded) {
        // JS already loaded — just re-init
        requestAnimationFrame(() => {
          document.dispatchEvent(new CustomEvent("mc:hero:reload"));
        });
      } else {
        // First time seeing a slider — dynamically load the script
        const heroScript = newHeroWrap.querySelector("script[src]");
        if (heroScript) {
          const s = document.createElement("script");
          s.src = heroScript.getAttribute("src");
          s.onload = () => {
            window._mcHeroJSLoaded = true;
          };
          document.head.appendChild(s);
        }
      }
    }
  }

  // Product count
  const newProductCount = doc.getElementById("ProductCount");
  const currentProductCount = document.getElementById("ProductCount");
  if (newProductCount && currentProductCount) {
    currentProductCount.innerHTML = newProductCount.innerHTML;
  }
};

/**
 * Show the loading overlay.
 * @returns {HTMLElement|null}
 */
const showLoading = () => {
  const overlay = document.getElementById("MCLoadingOverlay");
  if (overlay) overlay.classList.add("active");
  return overlay;
};

/**
 * Hide the loading overlay.
 * @param {HTMLElement|null} overlay
 */
const hideLoading = (overlay) => {
  if (overlay) overlay.classList.remove("active");
};

/**
 * Parse an HTML string into a Document.
 * @param {string} html
 * @returns {Document}
 */
const parseHTML = (html) => {
  const parser = new DOMParser();
  return parser.parseFromString(html, "text/html");
};

/* ========================================================================
   MCCollectionNav — Desktop Sidebar
   ======================================================================== */

class MCCollectionNav {
  /**
   * @param {HTMLElement} sidebar – The #MCCollectionNav element.
   */
  constructor(sidebar) {
    this.sidebar = sidebar;
    this._bindToggle();
    this._bindAjaxNav();
    this._bindShowcaseCards();
    this._bindPopstate();
  }

  /* ---- Toggle open/close sub-lists ----------------------------------- */

  _bindToggle() {
    this.sidebar.addEventListener("click", (e) => {
      const toggle = e.target.closest(".mc-collection-nav__toggle");
      if (!toggle) return;

      e.preventDefault();
      e.stopPropagation();

      const isOpen = toggle.classList.contains(
        "mc-collection-nav__toggle--open",
      );
      const parent = toggle.closest("li");
      const sublist =
        parent.querySelector(".mc-collection-nav__sublist") ||
        parent.querySelector(".mc-collection-nav__subsublist");

      if (!sublist) return;

      const isSubsub = sublist.classList.contains(
        "mc-collection-nav__subsublist",
      );
      const openClass = isSubsub
        ? "mc-collection-nav__subsublist--open"
        : "mc-collection-nav__sublist--open";

      if (isOpen) {
        toggle.classList.remove("mc-collection-nav__toggle--open");
        toggle.setAttribute("aria-expanded", "false");
        sublist.classList.remove(openClass);
      } else {
        toggle.classList.add("mc-collection-nav__toggle--open");
        toggle.setAttribute("aria-expanded", "true");
        sublist.classList.add(openClass);
      }
    });
  }

  /* ---- AJAX navigation (sidebar links) ------------------------------- */

  _bindAjaxNav() {
    this.sidebar.addEventListener("click", (e) => {
      const link = e.target.closest("[data-ajax-nav]");
      if (!link) return;
      if (e.metaKey || e.ctrlKey) return;

      e.preventDefault();
      this.navigate(link.href);
    });
  }

  /* ---- AJAX navigation (showcase cards in grid) ---------------------- */

  _bindShowcaseCards() {
    const grid = document.getElementById("ProductGridContainer");
    if (!grid) return;

    grid.addEventListener("click", (e) => {
      const link = e.target.closest("[data-ajax-nav]");
      if (!link) return;
      if (e.metaKey || e.ctrlKey) return;

      e.preventDefault();
      this.navigate(link.href);
    });
  }

  /* ---- Popstate (browser back/forward) ------------------------------- */

  _bindPopstate() {
    window.addEventListener("popstate", () => window.location.reload());
  }

  /* ---- Core AJAX navigate -------------------------------------------- */

  navigate(url) {
    const overlay = showLoading();
    this.sidebar.classList.add("is-loading");

    fetch(url)
      .then((res) => res.text())
      .then((html) => {
        const doc = parseHTML(html);

        // Swap product grid
        const newGrid = doc.getElementById("ProductGridContainer");
        const currentGrid = document.getElementById("ProductGridContainer");
        if (newGrid && currentGrid) {
          currentGrid.innerHTML = newGrid.innerHTML;
        }

        // Swap sidebar
        const newSidebar = doc.getElementById("MCCollectionNav");
        if (newSidebar) {
          this.sidebar.innerHTML = newSidebar.innerHTML;
        }

        // Common elements (title, hero, product count)
        updatePageElements(doc);

        // Facet bar visibility toggle
        this._updateFacetBar(doc);

        // Re-bind showcase card clicks on new grid content
        this._bindShowcaseCards();

        history.pushState({}, "", url);
        hideLoading(overlay);
      })
      .catch((err) => {
        console.error("MCCollectionNav AJAX error:", err);
        hideLoading(overlay);
        window.location.href = url;
      })
      .finally(() => {
        this.sidebar.classList.remove("is-loading");
      });
  }

  /* ---- Facet bar show/hide ------------------------------------------- */

  _updateFacetBar(doc) {
    const currentFacetBar = document.querySelector(".facets-vertical-sort");
    const newFacetBar = doc.querySelector(".facets-vertical-sort");

    if (currentFacetBar) {
      if (newFacetBar) {
        currentFacetBar.style.display = "";
        currentFacetBar.innerHTML = newFacetBar.innerHTML;
      } else {
        currentFacetBar.style.display = "none";
      }
    } else if (newFacetBar) {
      const facetsVertical = document.querySelector(
        ".facets-vertical.page-width",
      );
      if (facetsVertical) {
        facetsVertical.parentNode.insertBefore(newFacetBar, facetsVertical);
      }
    }
  }
}

/* ========================================================================
   MCCollectionSort — Desktop Sorting
   ======================================================================== */

class MCCollectionSort {
  /**
   * @param {NodeList} inputs – Radio inputs with name="sort_by_desktop".
   * @param {HTMLElement|null} sidebar – The sidebar element for loading state.
   */
  constructor(inputs, sidebar) {
    this.sidebar = sidebar;
    this._bind(inputs);
  }

  _bind(inputs) {
    inputs.forEach((input) => {
      input.addEventListener("change", (e) => {
        const url = new URL(window.location.href);
        url.searchParams.set("sort_by", e.target.value);

        const overlay = showLoading();
        if (this.sidebar) this.sidebar.classList.add("is-loading");

        fetch(url.toString())
          .then((res) => res.text())
          .then((html) => {
            const doc = parseHTML(html);

            const newGrid = doc.getElementById("ProductGridContainer");
            const currentGrid = document.getElementById("ProductGridContainer");
            if (newGrid && currentGrid) {
              currentGrid.innerHTML = newGrid.innerHTML;
            }

            const newSidebar = doc.getElementById("MCCollectionNav");
            if (newSidebar && this.sidebar) {
              this.sidebar.innerHTML = newSidebar.innerHTML;
            }

            updatePageElements(doc);
            history.pushState({}, "", url);
            hideLoading(overlay);
          })
          .catch((err) => {
            console.error("MCCollectionSort error:", err);
            window.location.href = url.toString();
          })
          .finally(() => {
            if (this.sidebar) this.sidebar.classList.remove("is-loading");
          });
      });
    });
  }
}

/* ========================================================================
   MCMobileDrawer — Mobile Navigation Drawer
   ======================================================================== */

class MCMobileDrawer {
  /**
   * @param {HTMLElement} drawer – The .mc-mobile-facets container.
   * @param {HTMLElement|null} sidebar – Desktop sidebar for syncing.
   */
  constructor(drawer, sidebar) {
    this.drawer = drawer;
    this.sidebar = sidebar;
    this.details = drawer.querySelector(".mc-mobile-facets__details");

    this._bindClose();
    this._bindSorting();
    this._bindAccordion();
    this._bindNavigation();
    this._bindDone();
  }

  /* ---- Close button -------------------------------------------------- */

  _bindClose() {
    const closeBtn = this.drawer.querySelector(".mc-mobile-facets__close");
    if (closeBtn) {
      closeBtn.addEventListener("click", () => {
        this.details.removeAttribute("open");
      });
    }
  }

  /* ---- Sorting -------------------------------------------------------- */

  _bindSorting() {
    const sortInputs = this.drawer.querySelectorAll(
      'input[name="sort_by_mobile"]',
    );

    sortInputs.forEach((input) => {
      input.addEventListener("change", (e) => {
        const url = new URL(window.location.href);
        url.searchParams.set("sort_by", e.target.value);

        this.details.removeAttribute("open");
        const overlay = showLoading();

        fetch(url.toString())
          .then((res) => res.text())
          .then((html) => {
            const doc = parseHTML(html);

            const newGrid = doc.getElementById("ProductGridContainer");
            const currentGrid = document.getElementById("ProductGridContainer");
            if (newGrid && currentGrid) {
              currentGrid.innerHTML = newGrid.innerHTML;
            }

            const newSidebar = doc.getElementById("MCCollectionNav");
            const currentSidebar = document.getElementById("MCCollectionNav");
            if (newSidebar && currentSidebar) {
              currentSidebar.innerHTML = newSidebar.innerHTML;
            }

            const newMobileDrawer = doc.querySelector(".mc-mobile-facets");
            const currentMobileDrawer =
              document.querySelector(".mc-mobile-facets");
            if (newMobileDrawer && currentMobileDrawer) {
              currentMobileDrawer.innerHTML = newMobileDrawer.innerHTML;
              // Reload to re-initialize all event listeners
              window.location.href = url.toString();
              return;
            }

            history.pushState({}, "", url);
            hideLoading(overlay);
          })
          .catch((err) => {
            console.error("MCMobileDrawer sort error:", err);
            window.location.href = url.toString();
          });
      });
    });
  }

  /* ---- Accordion toggle (capture phase) ------------------------------ */

  _bindAccordion() {
    this.drawer.addEventListener(
      "click",
      (e) => {
        const toggle = e.target.closest(".mc-mobile-drawer__toggle");
        if (!toggle) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const item = toggle.closest(".mc-mobile-drawer__item");
        const sublist = item.querySelector(".mc-mobile-drawer__sublist");
        if (!sublist) return;

        const isOpen = toggle.classList.contains("open");
        if (isOpen) {
          toggle.classList.remove("open");
          sublist.classList.remove("open");
        } else {
          toggle.classList.add("open");
          sublist.classList.add("open");
        }

        return false;
      },
      true, // capture phase
    );
  }

  /* ---- AJAX navigation from drawer links ----------------------------- */

  _bindNavigation() {
    this.drawer.addEventListener("click", (e) => {
      if (e.target.closest(".mc-mobile-drawer__toggle")) return;

      const link = e.target.closest("[data-mobile-nav]");
      if (!link) return;
      if (e.metaKey || e.ctrlKey) return;

      e.preventDefault();
      const url = link.href;

      this.details.removeAttribute("open");
      const overlay = showLoading();

      fetch(url)
        .then((res) => res.text())
        .then((html) => {
          const doc = parseHTML(html);

          const newGrid = doc.getElementById("ProductGridContainer");
          const currentGrid = document.getElementById("ProductGridContainer");
          if (newGrid && currentGrid) {
            currentGrid.innerHTML = newGrid.innerHTML;
          }

          if (this.sidebar) {
            const newSidebar = doc.getElementById("MCCollectionNav");
            if (newSidebar) {
              this.sidebar.innerHTML = newSidebar.innerHTML;
            }
          }

          const newMobileDrawer = doc.querySelector(".mc-mobile-facets");
          const currentMobileDrawer =
            document.querySelector(".mc-mobile-facets");
          if (newMobileDrawer && currentMobileDrawer) {
            // Reload to re-init all event listeners
            window.location.href = url;
            return;
          }

          updatePageElements(doc);
          history.pushState({}, "", url);
          hideLoading(overlay);
        })
        .catch((err) => {
          console.error("MCMobileDrawer AJAX error:", err);
          hideLoading(overlay);
          window.location.href = url;
        });
    });
  }

  /* ---- Done button --------------------------------------------------- */

  _bindDone() {
    const doneBtn = this.drawer.querySelector(".mc-mobile-drawer__done");
    if (doneBtn) {
      doneBtn.addEventListener("click", () => {
        this.details.removeAttribute("open");
      });
    }
  }
}

/* ========================================================================
   Auto-init on DOM ready
   ======================================================================== */

document.addEventListener("DOMContentLoaded", () => {
  const sidebar = document.getElementById("MCCollectionNav");

  // Desktop sidebar
  if (sidebar) {
    new MCCollectionNav(sidebar);
  }

  // Desktop sorting
  const desktopSortInputs = document.querySelectorAll(
    'input[name="sort_by_desktop"]',
  );
  if (desktopSortInputs.length > 0) {
    new MCCollectionSort(desktopSortInputs, sidebar);
  }

  // Mobile drawer
  const mobileDrawer = document.querySelector(".mc-mobile-facets");
  if (mobileDrawer) {
    new MCMobileDrawer(mobileDrawer, sidebar);
  }
});
